/**
 * UnverifiedEmailBanner — soft nudge for users whose email isn't
 * confirmed yet (Module 2.1 Frontend Phase 1 Part 3).
 *
 * Mounted once at the top of <Layout> so every authenticated page
 * inherits it. Renders nothing for already-verified users (or when
 * the auth store has no user). The user can:
 *
 *   - Click "Send a new link" → POST /auth/resend-verification
 *     (success / error feedback rendered inline; the button locks
 *     for 30s after success so an impatient user can't spam-resend)
 *   - Click "Dismiss" → hide for the rest of this session
 *     (sessionStorage; the banner returns on next login)
 *   - Click "Open verification page" → navigate to
 *     /verify-email-pending which has the same Resend + the email
 *     address callout in a fuller layout
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle, CheckCircle2, Loader2, Mail, X,
} from 'lucide-react'

import { useAuthStore } from '@/stores/authStore'
import { useResendVerification } from '@/hooks/useEmailVerification'
import { getErrorMessage } from '@/lib/api'


const DISMISS_KEY = 'injez:unverified-banner-dismissed'
const RESEND_LOCK_MS = 30_000


export function UnverifiedEmailBanner() {
  const { i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const resend = useResendVerification()
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem(DISMISS_KEY) === '1'
  })
  const [lastSentAt, setLastSentAt] = useState<number | null>(null)
  const [resendLocked, setResendLocked] = useState(false)

  // Re-enable the Resend button 30s after a successful send.
  useEffect(() => {
    if (!lastSentAt) return
    setResendLocked(true)
    const t = setTimeout(() => setResendLocked(false), RESEND_LOCK_MS)
    return () => clearTimeout(t)
  }, [lastSentAt])

  // Hide unless we have an authenticated, unverified user.
  if (!isAuthenticated || !user || user.is_verified === true) {
    return null
  }
  if (dismissed) return null

  const handleResend = async () => {
    try {
      await resend.mutateAsync()
      setLastSentAt(Date.now())
    } catch {
      /* error rendered below */
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(DISMISS_KEY, '1')
    }
  }

  return (
    <div
      role="alert"
      className="bg-amber-50 border-b border-amber-200 px-4 py-2.5"
    >
      <div className="max-w-7xl mx-auto flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start sm:items-center gap-3 min-w-0">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {isArabic
                ? 'لم يتم التحقق من بريدك بعد'
                : 'Your email isn\'t verified yet'}
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              {isArabic
                ? <>تحقق من رسالة التأكيد على{' '}<span dir="ltr" className="font-mono">{user.email}</span>{' '}أو اطلب رابطاً جديداً.</>
                : <>Check your inbox at{' '}<span dir="ltr" className="font-mono">{user.email}</span>{' '}or request a fresh link.</>}
            </p>
            {resend.isSuccess && lastSentAt && (
              <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                <span>
                  {isArabic
                    ? '✓ تم إرسال رابط جديد'
                    : '✓ A fresh link is on its way'}
                </span>
              </p>
            )}
            {resend.isError && (
              <p className="text-xs text-rose-700 mt-1">
                {getErrorMessage(resend.error)}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleResend}
            disabled={resend.isPending || resendLocked}
            className="text-xs font-semibold text-amber-900 hover:bg-amber-100 px-3 py-1.5 min-h-[40px] rounded inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resend.isPending
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Mail className="w-3.5 h-3.5" />}
            <span>
              {isArabic ? 'أرسل رابطاً جديداً' : 'Send a new link'}
            </span>
          </button>
          <Link
            to="/verify-email-pending"
            className="text-xs font-semibold text-amber-900 hover:underline px-2 py-1.5 min-h-[40px]"
          >
            {isArabic ? 'تفاصيل' : 'Details'}
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={isArabic ? 'إغلاق' : 'Dismiss'}
            className="text-amber-700 hover:bg-amber-100 p-1.5 min-h-[40px] min-w-[40px] rounded inline-flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
